import { useState, useEffect, useCallback } from 'react';
import { Download, TrendingUp, BarChart3, PieChart, Users, AlertTriangle, RefreshCw } from 'lucide-react';
import ReactApexChart from 'react-apexcharts';
import type { ApexOptions } from 'apexcharts';
import DataTable from '../../components/common/DataTable';
import type { Column } from '../../components/common/DataTable';
import { observabilityService } from '../../services/observability.service';
import { alertsService } from '../../services';
import { formatDate, formatTimeOnly, createChartTooltipFormatter } from '../../utils/formatDate';
import { useSettingsStore } from '../../stores/settingsStore';


type TimeRange = '1h' | '6h' | '24h' | '7d';

// Maximum bytes/sec per interface (10 Gbps) — filters out counter-wrap corruption
// from agent uint64 underflow when VPP counters reset. Actual edge NICs are 1–2.5 Gbps.
const MAX_IFACE_BYTES_PER_SEC = 1_250_000_000;

// Helper to format bytes
const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

// Adaptive bandwidth formatter — picks best unit from bps to Gbps
const formatBandwidth = (mbps: number): string => {
  if (mbps === 0) return '0 bps';
  const bps = mbps * 1_000_000;
  if (bps < 1_000) return `${bps.toFixed(0)} bps`;
  if (bps < 1_000_000) return `${(bps / 1_000).toFixed(1)} Kbps`;
  if (bps < 1_000_000_000) return `${(bps / 1_000_000).toFixed(2)} Mbps`;
  return `${(bps / 1_000_000_000).toFixed(2)} Gbps`;
};

// Determine the best unit and divisor for a dataset's Y-axis based on peak value
const getBandwidthScale = (peakMbps: number): { unit: string; divisor: number } => {
  const peakBps = peakMbps * 1_000_000;
  if (peakBps < 1_000) return { unit: 'bps', divisor: 1 / 1_000_000 };
  if (peakBps < 1_000_000) return { unit: 'Kbps', divisor: 1 / 1_000 };
  if (peakBps < 1_000_000_000) return { unit: 'Mbps', divisor: 1 };
  return { unit: 'Gbps', divisor: 1_000 };
};

// Protocol label → display name. Agents emit both numeric (`6`, `17`)
// and lowercase string (`tcp`, `udp`) variants depending on whether the
// flow hit a well-known proto lookup. Unclassified/control-plane flows
// land in proto="0" or "" — bucket them into "Other" so the panel
// stops rendering literal "0" chips.
const PROTOCOL_NAMES: Record<string, string> = {
  '0': 'Other',
  '1': 'ICMP',
  '6': 'TCP',
  '17': 'UDP',
  '47': 'GRE',
  '50': 'ESP',
  '58': 'ICMPv6',
  'icmp': 'ICMP',
  'tcp': 'TCP',
  'udp': 'UDP',
  'gre': 'GRE',
  'esp': 'ESP',
  'icmpv6': 'ICMPv6',
  '': 'Other',
};

// Well-known port to service mapping
const PORT_SERVICES: Record<number, string> = {
  22: 'SSH',
  53: 'DNS',
  80: 'HTTP',
  443: 'HTTPS',
  8080: 'HTTP-Alt',
  3389: 'RDP',
  25: 'SMTP',
  110: 'POP3',
  143: 'IMAP',
  993: 'IMAPS',
  995: 'POP3S',
  21: 'FTP',
  23: 'Telnet',
  3306: 'MySQL',
  5432: 'PostgreSQL',
  6379: 'Redis',
  27017: 'MongoDB',
};

interface TrafficByProtocol {
  protocol: string;
  protocol_name: string;
  total_bytes: number;
  percentage: number;
}

interface TrafficByPort {
  port: string;
  service_name: string;
  total_bytes: number;
  percentage: number;
}

interface TrafficAnomaly {
  id: string;
  type: string;
  severity: string;
  description: string;
  timestamp: string;
  device: string;
}

interface EdgeTrafficSummary {
  edgeId: string;
  edgeName: string;
  totalBytesRx: number;
  totalBytesTx: number;
  avgBandwidthMbps: number;
  hasTraffic: boolean;
}

const TrafficAnalytics = () => {
  const { getDateFormatSettings } = useSettingsStore();
  const dateSettings = getDateFormatSettings();
  const [timeRange, setTimeRange] = useState<TimeRange>('24h');
  const [loading, setLoading] = useState(true);
  const [lastRefreshed, setLastRefreshed] = useState(new Date());

  // Data states
  const [trafficTimeSeries, setTrafficTimeSeries] = useState<any[]>([]);
  const [edgeTrafficSummary, setEdgeTrafficSummary] = useState<EdgeTrafficSummary[]>([]);
  const [trafficAnomalies, setTrafficAnomalies] = useState<TrafficAnomaly[]>([]);

  // IPFIX flow data (from Prometheus metrics)
  const [trafficByProtocol, setTrafficByProtocol] = useState<TrafficByProtocol[]>([]);
  const [trafficByPort, setTrafficByPort] = useState<TrafficByPort[]>([]);
  const [hasFlowData, setHasFlowData] = useState(false);

  // Stats
  const [totalTrafficBytes, setTotalTrafficBytes] = useState(0);
  const [avgDailyTrafficBytes, setAvgDailyTrafficBytes] = useState(0);
  const [peakBandwidthMbps, setPeakBandwidthMbps] = useState(0);
  const [onlineDevices, setOnlineDevices] = useState(0);

  // Filter states
  const [hideNoTraffic, setHideNoTraffic] = useState(false);

  // Parse time range to seconds
  const getTimeRangeSeconds = (range: TimeRange): number => {
    const unit = range.slice(-1);
    const value = parseInt(range.slice(0, -1));
    switch (unit) {
      case 'h': return value * 3600;
      case 'd': return value * 86400;
      default: return 86400;
    }
  };

  // Fetch all data
  const fetchData = useCallback(async (isBackground = false) => {
    if (!isBackground) setLoading(true);
    const options = isBackground ? { showLoading: false } : undefined;

    try {
      const end = Math.floor(Date.now() / 1000);
      const rangeSeconds = getTimeRangeSeconds(timeRange);
      const start = end - rangeSeconds;
      const step = Math.floor(rangeSeconds / 100);

      // Fetch edges
      const edgesRes = await observabilityService.getRegisteredEdges();
      const edgeList = edgesRes.status === 'success' ? edgesRes.data || [] : [];
      setOnlineDevices(edgeList.length);

      if (edgeList.length === 0) {
        if (!isBackground) setLoading(false);
        return;
      }

      // Aggregate traffic across all edges
      // Use rate for charts to get bandwidth (Mbps) directly from Prometheus
      // Use increase for total traffic metrics to avoid manual diff spikes
      // clamp_max filters counter-wrap corruption from agent uint64 underflow on VPP restart
      //
      // Filter by the semantic `role` label (wan/lan) emitted by the agent,
      // not by an interface-name regex. This survives DPDK/AF_PACKET/TAP
      // naming differences and future cloud provider NIC naming changes.
      const maxIncrease = MAX_IFACE_BYTES_PER_SEC * rangeSeconds;
      const [rxRateRes, txRateRes, rxIncreaseRes, txIncreaseRes] = await Promise.all([
        observabilityService.queryRange(`sum(clamp_max(rate(edge_interface_rx_bytes_total{role=~"wan|lan"}[${Math.max(step, 60)}s]), ${MAX_IFACE_BYTES_PER_SEC}))`, start, end, step, options),
        observabilityService.queryRange(`sum(clamp_max(rate(edge_interface_tx_bytes_total{role=~"wan|lan"}[${Math.max(step, 60)}s]), ${MAX_IFACE_BYTES_PER_SEC}))`, start, end, step, options),
        observabilityService.query(`sum(clamp_max(increase(edge_interface_rx_bytes_total{role=~"wan|lan"}[${timeRange}]), ${maxIncrease}))`, undefined, options),
        observabilityService.query(`sum(clamp_max(increase(edge_interface_tx_bytes_total{role=~"wan|lan"}[${timeRange}]), ${maxIncrease}))`, undefined, options),
      ]);

      // Process traffic time series (convert bytes/sec to Mbps)
      const timeSeries: any[] = [];
      let peakMbps = 0;

      if (rxRateRes.status === 'success' && rxRateRes.data?.result?.length > 0) {
        const rxValues = rxRateRes.data.result[0].values || [];
        const txValues = txRateRes.data?.result?.[0]?.values || [];

        for (let i = 0; i < rxValues.length; i++) {
          const [ts, rxRate] = rxValues[i];
          const txRate = i < txValues.length ? parseFloat(txValues[i][1]) : 0;

          const rxMbps = (parseFloat(rxRate) * 8) / 1000000;
          const txMbps = (txRate * 8) / 1000000;
          const totalMbps = rxMbps + txMbps;

          peakMbps = Math.max(peakMbps, totalMbps);

          timeSeries.push({
            timestamp: ts * 1000,
            value: totalMbps,
            rx: rxMbps,
            tx: txMbps,
          });
        }
      }

      setTrafficTimeSeries(timeSeries);
      setPeakBandwidthMbps(Math.round(peakMbps * 10) / 10);

      // Parse total increase in bytes
      let totalRxBytes = 0;
      let totalTxBytes = 0;

      if (rxIncreaseRes.status === 'success' && rxIncreaseRes.data?.result?.length > 0) {
        totalRxBytes = parseFloat(rxIncreaseRes.data.result[0].value?.[1] || '0');
      }
      if (txIncreaseRes.status === 'success' && txIncreaseRes.data?.result?.length > 0) {
        totalTxBytes = parseFloat(txIncreaseRes.data.result[0].value?.[1] || '0');
      }

      // Keep totals as raw bytes — the render path calls formatBytes()
      // so the unit adapts (B / KB / MB / GB) instead of always showing
      // GB with a leading zero on fleets that only push a few MB/day.
      const totalBytes = totalRxBytes + totalTxBytes;
      setTotalTrafficBytes(totalBytes);

      const days = rangeSeconds / 86400;
      setAvgDailyTrafficBytes(days > 0 ? totalBytes / days : 0);

      // Top applications populated from IPFIX port data below (VPP app stats table is unpopulated)

      // Fetch IPFIX flow data via PromQL
      // Query traffic by protocol
      const protocolQuery = `sum by (protocol) (increase(edge_ipfix_bytes_total[${timeRange}]))`;
      const portQuery = `topk(10, sum by (dst_port) (increase(edge_ipfix_bytes_total[${timeRange}])))`;

      const [protocolRes, portRes] = await Promise.all([
        observabilityService.query(protocolQuery, undefined, options),
        observabilityService.query(portQuery, undefined, options),
      ]);

      let foundFlowData = false;

      // Process protocol data. Drop 0-byte buckets — the agent always
      // emits a default `protocol="0"` counter so the series exists,
      // but rendering it as "0 B, 0%" just clutters the panel on idle
      // fleets. Real traffic always produces non-zero deltas.
      if (protocolRes.status === 'success' && protocolRes.data?.result?.length > 0) {
        const protocols: TrafficByProtocol[] = [];
        let totalProtoBytes = 0;

        for (const result of protocolRes.data.result) {
          const bytes = parseFloat(result.value?.[1] || '0');
          if (bytes <= 0) continue;
          totalProtoBytes += bytes;
          const rawProto = String(result.metric?.protocol ?? '');
          const protoKey = rawProto.toLowerCase();
          protocols.push({
            protocol: rawProto || 'unknown',
            protocol_name: PROTOCOL_NAMES[protoKey] || PROTOCOL_NAMES[rawProto] || rawProto.toUpperCase() || 'Unknown',
            total_bytes: bytes,
            percentage: 0,
          });
        }

        // Calculate percentages
        for (const proto of protocols) {
          proto.percentage = totalProtoBytes > 0 ? (proto.total_bytes / totalProtoBytes) * 100 : 0;
        }

        protocols.sort((a, b) => b.total_bytes - a.total_bytes);
        setTrafficByProtocol(protocols);
        if (protocols.length > 0) foundFlowData = true;
      }

      // Process port data. Same 0-byte filter as protocol buckets —
      // dst_port="0" is the default catch-all and is always emitted.
      if (portRes.status === 'success' && portRes.data?.result?.length > 0) {
        const ports: TrafficByPort[] = [];
        let totalPortBytes = 0;

        for (const result of portRes.data.result) {
          const bytes = parseFloat(result.value?.[1] || '0');
          if (bytes <= 0) continue;
          const rawPort = result.metric?.dst_port || '';
          const portNum = parseInt(rawPort, 10);
          let serviceName: string;
          if (rawPort === '0' || rawPort === '') {
            serviceName = 'Other';
          } else if (rawPort === 'high') {
            serviceName = 'Ephemeral (≥1024)';
          } else {
            serviceName = PORT_SERVICES[portNum] || `Port ${rawPort}`;
          }
          totalPortBytes += bytes;
          ports.push({
            port: rawPort || 'unknown',
            service_name: serviceName,
            total_bytes: bytes,
            percentage: 0,
          });
        }

        // Calculate percentages
        for (const port of ports) {
          port.percentage = totalPortBytes > 0 ? (port.total_bytes / totalPortBytes) * 100 : 0;
        }

        ports.sort((a, b) => b.total_bytes - a.total_bytes);
        setTrafficByPort(ports);
        if (ports.length > 0) foundFlowData = true;
      }

      setHasFlowData(foundFlowData);

      // Fetch per-edge traffic summary
      const edgeSummaries: EdgeTrafficSummary[] = [];
      for (const edge of edgeList) {
        try {
          const [edgeRxRes, edgeTxRes] = await Promise.all([
            observabilityService.query(`sum(clamp_max(increase(edge_interface_rx_bytes_total{edge_id="${edge.id}", role=~"wan|lan"}[${timeRange}]), ${maxIncrease}))`, undefined, options),
            observabilityService.query(`sum(clamp_max(increase(edge_interface_tx_bytes_total{edge_id="${edge.id}", role=~"wan|lan"}[${timeRange}]), ${maxIncrease}))`, undefined, options),
          ]);

          let edgeRxTotal = 0;
          let edgeTxTotal = 0;

          if (edgeRxRes.status === 'success' && edgeRxRes.data?.result?.length > 0) {
            edgeRxTotal = parseFloat(edgeRxRes.data.result[0].value?.[1] || '0');
          }
          if (edgeTxRes.status === 'success' && edgeTxRes.data?.result?.length > 0) {
            edgeTxTotal = parseFloat(edgeTxRes.data.result[0].value?.[1] || '0');
          }

          const totalBytesEdge = edgeRxTotal + edgeTxTotal;
          const avgBw = rangeSeconds > 0 ? (totalBytesEdge * 8) / rangeSeconds / 1000000 : 0;

          const hasTraffic = totalBytesEdge > 0;
          edgeSummaries.push({
            edgeId: edge.id,
            edgeName: edge.name,
            totalBytesRx: edgeRxTotal,
            totalBytesTx: edgeTxTotal,
            avgBandwidthMbps: Math.round(avgBw * 100) / 100,
            hasTraffic,
          });
        } catch {
          // Skip edge on error
        }
      }

      setEdgeTrafficSummary(edgeSummaries.sort((a, b) =>
        (b.totalBytesRx + b.totalBytesTx) - (a.totalBytesRx + a.totalBytesTx)
      ));

      // Fetch traffic anomalies from alerts
      try {
        const alerts = await alertsService.getAlerts();
        const anomalies: TrafficAnomaly[] = alerts
          .filter(alert => alert.status.state === 'active')
          .map(alert => ({
            id: alert.fingerprint,
            type: alert.labels.alertname?.includes('High') ? 'Spike' :
              alert.labels.alertname?.includes('Low') ? 'Drop' : 'Unusual',
            severity: alert.labels.severity === 'critical' ? 'High' :
              alert.labels.severity === 'warning' ? 'Medium' : 'Low',
            description: alert.annotations.summary || alert.annotations.description || 'Alert triggered',
            timestamp: formatTimestamp(alert.startsAt),
            device: alert.labels.edge_name || alert.labels.edge_id || 'Network',
          }));
        setTrafficAnomalies(anomalies);
      } catch {
        setTrafficAnomalies([]);
      }

      setLastRefreshed(new Date());
    } catch (error) {
      console.error('Failed to fetch traffic analytics:', error);
    } finally {
      if (!isBackground) setLoading(false);
    }
  }, [timeRange]);

  const formatTimestamp = (timestamp: string): string => {
    return formatDate(new Date(timestamp), dateSettings);
  };

  useEffect(() => {
    fetchData(false);
    const interval = setInterval(() => fetchData(true), 60000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Determine best Y-axis unit from peak traffic value
  const trafficPeak = trafficTimeSeries.reduce((max, d) => Math.max(max, d.rx, d.tx), 0);
  const { unit: bwUnit, divisor: bwDivisor } = getBandwidthScale(trafficPeak);

  // Traffic Over Time Chart
  const trafficTimeSeriesOptions: ApexOptions = {
    chart: {
      toolbar: { show: false },
      zoom: { enabled: false },
    },
    dataLabels: { enabled: false },
    stroke: { curve: 'smooth', width: 3 },
    colors: ['#3b82f6', '#10b981'],
    fill: { type: 'none' },
    grid: {
      borderColor: '#f1f5f9',
      strokeDashArray: 4,
    },
    xaxis: {
      type: 'datetime',
      labels: {
        datetimeUTC: false,
        style: { colors: '#64748b', fontSize: '11px' },
      },
    },
    yaxis: {
      title: { text: `Bandwidth (${bwUnit})`, style: { color: '#64748b' } },
      labels: {
        style: { colors: '#64748b', fontSize: '11px' },
        formatter: (val) => {
          const scaled = val / bwDivisor;
          return scaled < 10 ? scaled.toFixed(2) : scaled < 100 ? scaled.toFixed(1) : scaled.toFixed(0);
        },
      },
    },
    tooltip: {
      x: { formatter: createChartTooltipFormatter(dateSettings) },
      y: { formatter: (val: number) => formatBandwidth(val) },
    },
    legend: {
      position: 'top',
    },
  };

  const trafficTimeSeriesSeries = [
    {
      name: 'Download',
      data: trafficTimeSeries.map(d => ({
        x: d.timestamp,
        y: d.rx,
      })),
    },
    {
      name: 'Upload',
      data: trafficTimeSeries.map(d => ({
        x: d.timestamp,
        y: d.tx,
      })),
    },
  ];

  // Traffic by Edge - Bar Chart
  const edgeTrafficChartOptions: ApexOptions = {
    chart: {
      type: 'bar',
      toolbar: { show: false },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '60%',
        borderRadius: 4,
      },
    },
    dataLabels: { enabled: false },
    colors: ['#3b82f6'],
    xaxis: {
      categories: edgeTrafficSummary.slice(0, 10).map(e => e.edgeName.slice(0, 15)),
      labels: {
        style: { colors: '#64748b', fontSize: '11px' },
        rotate: -45,
      },
    },
    yaxis: {
      title: { text: 'Traffic (GB)', style: { color: '#64748b' } },
      labels: {
        style: { colors: '#64748b', fontSize: '11px' },
        formatter: (val) => (val / (1024 * 1024 * 1024)).toFixed(2),
      },
    },
    grid: {
      borderColor: '#f1f5f9',
      strokeDashArray: 4,
    },
  };

  const edgeTrafficChartSeries = [
    {
      name: 'Traffic',
      data: edgeTrafficSummary.slice(0, 10).map(e => e.totalBytesRx + e.totalBytesTx),
    },
  ];

  // Traffic by Protocol - Pie Chart
  const protocolChartOptions: ApexOptions = {
    chart: { type: 'donut' },
    labels: trafficByProtocol.map(p => p.protocol_name),
    colors: ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444', '#06b6d4'],
    legend: {
      position: 'bottom',
      fontSize: '12px',
    },
    dataLabels: {
      enabled: true,
      formatter: (val: number) => `${val.toFixed(1)}%`,
    },
    plotOptions: {
      pie: {
        donut: {
          size: '65%',
          labels: {
            show: true,
            total: {
              show: true,
              label: 'Protocols',
              fontSize: '14px',
              formatter: () => `${trafficByProtocol.length}`,
            },
          },
        },
      },
    },
    tooltip: {
      y: {
        formatter: (val: number, opts?: { seriesIndex: number }) => {
          if (!opts) return `${val.toFixed(1)}%`;
          const proto = trafficByProtocol[opts.seriesIndex];
          return proto ? formatBytes(proto.total_bytes) : `${val.toFixed(1)}%`;
        },
      },
    },
  };

  const protocolChartSeries = trafficByProtocol.map(p => p.percentage);

  // Traffic by Port - Bar Chart
  const portChartOptions: ApexOptions = {
    chart: {
      type: 'bar',
      toolbar: { show: false },
    },
    plotOptions: {
      bar: {
        horizontal: true,
        barHeight: '70%',
        borderRadius: 4,
      },
    },
    dataLabels: {
      enabled: true,
      formatter: (val: number) => formatBytes(val),
      style: { fontSize: '10px' },
    },
    colors: ['#3b82f6'],
    xaxis: {
      labels: {
        style: { colors: '#64748b', fontSize: '11px' },
        formatter: (val) => formatBytes(Number(val)),
      },
    },
    yaxis: {
      labels: {
        style: { colors: '#64748b', fontSize: '11px' },
      },
    },
    grid: {
      borderColor: '#f1f5f9',
      strokeDashArray: 4,
    },
    tooltip: {
      y: {
        formatter: (val: number) => formatBytes(val),
      },
    },
  };

  const portChartCategories = trafficByPort.slice(0, 10).map(p => p.service_name);
  const portChartSeries = [
    {
      name: 'Traffic',
      data: trafficByPort.slice(0, 10).map(p => p.total_bytes),
    },
  ];

  // Edge Traffic Table Columns
  const edgeTrafficColumns: Column<EdgeTrafficSummary>[] = [
    {
      key: 'edgeName',
      label: 'Edge Device',
      sortable: true,
      render: (edge) => (
        <div>
          <div className="font-medium text-gray-900">{edge.edgeName}</div>
          <div className="text-xs text-gray-500 font-mono">{edge.edgeId.slice(0, 8)}...</div>
        </div>
      ),
    },
    {
      key: 'totalBytesRx',
      label: 'Download',
      sortable: true,
      render: (edge) => (
        <span className="text-sm text-gray-900">{formatBytes(edge.totalBytesRx)}</span>
      ),
    },
    {
      key: 'totalBytesTx',
      label: 'Upload',
      sortable: true,
      render: (edge) => (
        <span className="text-sm text-gray-900">{formatBytes(edge.totalBytesTx)}</span>
      ),
    },
    {
      key: 'avgBandwidthMbps',
      label: 'Avg Bandwidth',
      sortable: true,
      render: (edge) => (
        <span className="font-semibold text-primary-600">{formatBandwidth(edge.avgBandwidthMbps)}</span>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Traffic Analytics</h1>
          <p className="text-gray-600 mt-1">Insights and trends across your network</p>
        </div>
        <div className="flex items-center space-x-3">
          <span className="text-sm text-gray-500">
            Updated: {formatTimeOnly(lastRefreshed, dateSettings)}
          </span>
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value as TimeRange)}
            className="input-base"
          >
            <option value="1h">Last Hour</option>
            <option value="6h">Last 6 Hours</option>
            <option value="24h">Last 24 Hours</option>
            <option value="7d">Last 7 Days</option>
          </select>
          <button
            onClick={() => fetchData(false)}
            disabled={loading}
            className="btn-secondary inline-flex items-center space-x-2"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
          <button className="btn-primary inline-flex items-center space-x-2">
            <Download className="w-4 h-4" />
            <span>Export Report</span>
          </button>
        </div>
      </div>

      {/* Metric Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Traffic</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {formatBytes(totalTrafficBytes)}
              </p>
              <p className="text-xs text-gray-500 mt-1">Last {timeRange}</p>
            </div>
            <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-primary-700 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Avg Daily Traffic</p>
              <p className="text-2xl font-bold text-success-600 mt-1">
                {formatBytes(avgDailyTrafficBytes)}
              </p>
              <p className="text-xs text-gray-500 mt-1">Per day</p>
            </div>
            <div className="w-12 h-12 bg-gradient-to-br from-success-500 to-success-700 rounded-lg flex items-center justify-center">
              <BarChart3 className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Peak Bandwidth</p>
              <p className="text-2xl font-bold text-warning-600 mt-1">
                {formatBandwidth(peakBandwidthMbps)}
              </p>
              <p className="text-xs text-gray-500 mt-1">Highest recorded</p>
            </div>
            <div className="w-12 h-12 bg-gradient-to-br from-warning-500 to-warning-700 rounded-lg flex items-center justify-center">
              <PieChart className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Edge Devices</p>
              <p className="text-2xl font-bold text-purple-600 mt-1">{onlineDevices}</p>
              <p className="text-xs text-gray-500 mt-1">Reporting traffic</p>
            </div>
            <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-700 rounded-lg flex items-center justify-center">
              <Users className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>
      </div>

      {/* Traffic Over Time */}
      <div className="card">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Network Traffic Trends</h3>
        {loading ? (
          <div className="h-80 flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
          </div>
        ) : trafficTimeSeries.length === 0 ? (
          <div className="h-80 flex items-center justify-center text-gray-500">
            <p>No traffic data available for the selected time range</p>
          </div>
        ) : (
          <ReactApexChart
            options={trafficTimeSeriesOptions}
            series={trafficTimeSeriesSeries}
            type="line"
            height={320}
          />
        )}
      </div>

      {/* IPFIX Flow Data (via PromQL) */}
      {hasFlowData && (
        <>
          <div className="bg-green-50 border-l-4 border-green-400 p-4">
            <div className="flex">
              <div className="ml-3">
                <p className="text-sm text-green-700 font-medium">
                  <strong>Live Flow Data:</strong> IPFIX flow metrics are being collected from edge devices.
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Traffic by Protocol */}
            <div className="card">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Traffic by Protocol
                <span className="ml-2 text-xs font-normal text-green-600 bg-green-100 px-2 py-0.5 rounded">IPFIX</span>
              </h3>
              {loading ? (
                <div className="h-80 flex items-center justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
                </div>
              ) : trafficByProtocol.length === 0 ? (
                <div className="h-80 flex flex-col items-center justify-center text-gray-500">
                  <PieChart className="w-12 h-12 text-gray-300 mb-4" />
                  <p className="text-sm">No protocol data available</p>
                </div>
              ) : (
                <ReactApexChart
                  options={protocolChartOptions}
                  series={protocolChartSeries}
                  type="donut"
                  height={350}
                />
              )}
            </div>

            {/* Traffic by Port */}
            <div className="card">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Top Services by Traffic
                <span className="ml-2 text-xs font-normal text-green-600 bg-green-100 px-2 py-0.5 rounded">IPFIX</span>
              </h3>
              {loading ? (
                <div className="h-80 flex items-center justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
                </div>
              ) : trafficByPort.length === 0 ? (
                <div className="h-80 flex flex-col items-center justify-center text-gray-500">
                  <BarChart3 className="w-12 h-12 text-gray-300 mb-4" />
                  <p className="text-sm">No port data available</p>
                </div>
              ) : (
                <ReactApexChart
                  options={{
                    ...portChartOptions,
                    xaxis: {
                      ...portChartOptions.xaxis,
                      categories: portChartCategories,
                    },
                  }}
                  series={portChartSeries}
                  type="bar"
                  height={350}
                />
              )}
            </div>
          </div>

          {/* Port/Service Details Table */}
          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Service Traffic Details
              <span className="ml-2 text-xs font-normal text-green-600 bg-green-100 px-2 py-0.5 rounded">IPFIX</span>
            </h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Service/Port</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Traffic</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">% of Total</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {trafficByPort.map((port, idx) => (
                    <tr key={idx} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">
                        {port.service_name}
                        <span className="ml-2 text-xs text-gray-500">({port.port})</span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 text-right font-mono">{formatBytes(port.total_bytes)}</td>
                      <td className="px-4 py-3 text-sm text-right">
                        <div className="flex items-center justify-end space-x-2">
                          <div className="w-16 flex-shrink-0 bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-primary-500 h-2 rounded-full"
                              style={{ width: `${Math.min(port.percentage, 100)}%` }}
                            />
                          </div>
                          <span className="w-14 text-right tabular-nums text-gray-700 font-medium">{port.percentage.toFixed(1)}%</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Traffic by Edge - Full Width */}
      <div className="card">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Traffic by Edge Device</h3>
        {loading ? (
          <div className="h-80 flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
          </div>
        ) : edgeTrafficSummary.length === 0 ? (
          <div className="h-80 flex items-center justify-center text-gray-500">
            <p>No edge traffic data available</p>
          </div>
        ) : (
          <ReactApexChart
            options={edgeTrafficChartOptions}
            series={edgeTrafficChartSeries}
            type="bar"
            height={350}
          />
        )}
      </div>

      {/* Edge Traffic Table */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Edge Device Traffic Summary</h3>
          <label className="flex items-center space-x-2 text-sm text-gray-600">
            <input
              type="checkbox"
              checked={hideNoTraffic}
              onChange={(e) => setHideNoTraffic(e.target.checked)}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span>Hide devices with no traffic</span>
          </label>
        </div>
        <DataTable
          columns={edgeTrafficColumns}
          data={hideNoTraffic ? edgeTrafficSummary.filter(e => e.hasTraffic) : edgeTrafficSummary}
          loading={loading}
          searchable
          searchPlaceholder="Search edges..."
        />
      </div>

      {/* Traffic Anomalies */}
      <div className="card">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Active Alerts</h3>
        {trafficAnomalies.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <AlertTriangle className="w-12 h-12 text-gray-300 mx-auto mb-2" />
            <p className="text-sm">No active traffic alerts</p>
          </div>
        ) : (
          <div className="space-y-3">
            {trafficAnomalies.map((anomaly) => (
              <div
                key={anomaly.id}
                className={`flex items-start space-x-3 p-4 rounded-lg border ${anomaly.severity === 'High'
                  ? 'bg-danger-50 border-danger-200'
                  : anomaly.severity === 'Medium'
                    ? 'bg-warning-50 border-warning-200'
                    : 'bg-blue-50 border-blue-200'
                  }`}
              >
                <AlertTriangle
                  className={`w-5 h-5 mt-0.5 ${anomaly.severity === 'High'
                    ? 'text-danger-600'
                    : anomaly.severity === 'Medium'
                      ? 'text-warning-600'
                      : 'text-blue-600'
                    }`}
                />
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ${anomaly.type === 'Spike'
                          ? 'bg-danger-100 text-danger-700'
                          : anomaly.type === 'Drop'
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-warning-100 text-warning-700'
                          }`}
                      >
                        {anomaly.type}
                      </span>
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ${anomaly.severity === 'High'
                          ? 'bg-danger-100 text-danger-700'
                          : anomaly.severity === 'Medium'
                            ? 'bg-warning-100 text-warning-700'
                            : 'bg-blue-100 text-blue-700'
                          }`}
                      >
                        {anomaly.severity}
                      </span>
                    </div>
                    <span className="text-xs text-gray-500">{anomaly.timestamp}</span>
                  </div>
                  <p className="text-sm font-medium text-gray-900 mt-2">{anomaly.description}</p>
                  <p className="text-xs text-gray-600 mt-1">Device: {anomaly.device}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TrafficAnalytics;
